import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useMemo } from 'react';

import type { NotificationTemplate } from './helpers/types';
import {
  useUserNotificationsFilters,
  useUserNotificationsQuery,
  useUserNotificationTemplates,
  useUserNotificationUpdateStatus,
} from './hooks';
import type { NotificationsFilters } from './types';

interface UserNotificationsContextType {
  notifications: NotificationTemplate[];
  totalNotifications: number;
  unreadNotifications: number;
  fetchNotifications: (id: string, pageSize?: number) => void;
  fetchNextPage: (id: string, pageSize?: number) => void;
  startRealtimeUpdates: (id: string, pageSize?: number) => void;
  stopRealtimeUpdates: () => void;
  isLoading: boolean;
  filters: NotificationsFilters;
  setFilters: Dispatch<SetStateAction<NotificationsFilters>>;
  filteredNotifications: NotificationTemplate[];
  hasActiveFilters: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
}

export const UserNotificationsContext = createContext<UserNotificationsContextType | undefined>(
  undefined
);

interface UserNotificationsProviderProps {
  children: ReactNode;
}

export const UserNotificationsProvider = ({
  children,
}: UserNotificationsProviderProps): JSX.Element => {
  const {
    notifications,
    totalNotifications,
    unreadNotifications,
    fetchNotifications,
    startRealtimeUpdates,
    stopRealtimeUpdates,
    fetchNextPage,
    isLoading,
  } = useUserNotificationsQuery();

  const notificationsTemplates = useUserNotificationTemplates({
    notifications,
  });

  const { filters, setFilters, filteredNotifications, hasActiveFilters } =
    useUserNotificationsFilters({ notifications: notificationsTemplates });

  const { markAsRead, markAllAsRead, markAsUnread } = useUserNotificationUpdateStatus();

  const allNotificationsIds = notifications?.map((notification) => notification.getId());
  const markAllNotificationsAsRead = useCallback(
    () => markAllAsRead(allNotificationsIds),
    [allNotificationsIds, markAllAsRead]
  );

  const value = useMemo(
    () => ({
      notifications: notificationsTemplates,
      totalNotifications,
      unreadNotifications,
      fetchNotifications,
      fetchNextPage,
      startRealtimeUpdates,
      stopRealtimeUpdates,
      isLoading,
      filters,
      setFilters,
      filteredNotifications,
      hasActiveFilters,
      markAsRead,
      markAllAsRead: markAllNotificationsAsRead,
      markAsUnread,
    }),
    [
      fetchNotifications,
      fetchNextPage,
      filteredNotifications,
      filters,
      hasActiveFilters,
      isLoading,
      markAllNotificationsAsRead,
      markAsRead,
      markAsUnread,
      notificationsTemplates,
      setFilters,
      startRealtimeUpdates,
      stopRealtimeUpdates,
      totalNotifications,
      unreadNotifications,
    ]
  );

  return (
    <UserNotificationsContext.Provider value={value}>{children}</UserNotificationsContext.Provider>
  );
};
